import { ApiProjects } from 'api';
import { axiosToken } from 'api/apiBase/axios';
import { IBaseResponse } from 'api/apiBase/IBaseResponse';
import { mapError } from 'api/apiBase/mapError';
import { mapResponse } from 'api/apiBase/mapResponse';
import { Response } from 'api/apiBase/Response';

import {
  AddDexPairRequest,
  AddDexPairResponse,
  GetProjectByIdRequest,
  GetProjectWalletsRequest,
  GetProjectWalletsResponse,
  ProjectById,
} from './api.types';

export async function getProjectById({
  projectId,
}: GetProjectByIdRequest): Promise<Response<ProjectById>> {
  return ApiProjects.getProjectById(projectId);
}

/**
 * Adds a decentralized exchange (DEX) pair to a project.
 *
 * @param params - The request parameters
 * @param params.projectId - The ID of the project to add the DEX pair to
 * @param params.dex - The name of the DEX (e.g., "uniswap", "pancakeswap")
 * @param params.fee - The trading fee for the pair (e.g., 0.3 for 0.3%)
 * @param params.token_base - The base token symbol (e.g., "USDT")
 * @param params.token_quote - The quote token symbol (e.g., "ETH")
 * @param params.extra - Optional additional metadata for the pair
 * @param params.pool_id - Optional pool identifier data
 * @param params.notes - Optional notes about the pair
 *
 * @returns Promise resolving to the response containing the created pair ID
 *
 * @example
 * ```typescript
 * const response = await addDexPair({
 *   projectId: 123,
 *   dex: 'uniswap',
 *   fee: 0.3,
 *   token_base: 'USDT',
 *   token_quote: 'ETH',
 *   notes: 'Main trading pair',
 * });
 * console.log('Created pair with ID:', response.data.id);
 * ```
 *
 * @see https://api.dev.111pg.info/swagger/#/projects/ProjectAddDexPairRequest
 */
export async function addDexPair({
  projectId,
  ...params
}: AddDexPairRequest): Promise<Response<AddDexPairResponse>> {
  try {
    const response = await axiosToken.post<IBaseResponse<AddDexPairResponse>>(
      `/api/projects/${projectId}/dex-pairs`,
      params,
    );

    return mapResponse({ data: response.data });
  } catch (error: unknown) {
    return mapError<AddDexPairResponse>({ error });
  }
}

/**
 * Lists wallets connected to the pairs of the project.
 *
 * @param params - The request parameters
 * @param params.projectId - The ID of the project
 * @param params.limit - Optional limit for pagination
 * @param params.lastSeenId - Optional last seen ID for pagination
 * @param signal - Optional abort controller signal for request cancellation
 *
 * @returns Promise resolving to the response containing wallets list
 *
 * @see https://api.dev.111pg.info/swagger/#/projects/ProjectWalletsListRequest
 */
export async function getProjectWallets(
  { projectId, limit, lastSeenId }: GetProjectWalletsRequest,
  signal?: AbortSignal,
): Promise<Response<GetProjectWalletsResponse>> {
  try {
    const urlParams = new URLSearchParams();

    if (limit !== undefined) {
      urlParams.append('limit', limit.toString());
    }
    if (lastSeenId !== undefined) {
      urlParams.append('last_seen_id', lastSeenId.toString());
    }

    const queryString = urlParams.toString();
    const url = `/api/projects/${projectId}/wallets${queryString ? `?${queryString}` : ''}`;

    const response = await axiosToken.get<IBaseResponse<GetProjectWalletsResponse>>(url, {
      signal,
    });

    return mapResponse({ data: response.data });
  } catch (error: unknown) {
    return mapError<GetProjectWalletsResponse>({ error });
  }
}
